import {
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Stack,
  Typography
} from '@mui/material'
import React from 'react'
import { Link as RouterLink } from 'react-router-dom'

import Icon from 'components/Icon'
import useAppRefresh from 'hooks/use-app-refresh'
import useAuth from 'hooks/use-auth'
import { useGetUserSettingsMenuOptions } from 'hooks/use-get-user-settings-menu-options'

import type {
  UserSession,
  UserSessionTeam
} from 'types/api/users/sessions/user-session'

export const MainMenuPanel: React.FC<{
  currentTeam: UserSessionTeam
  session: UserSession
  onShowTeamSelectionMenuPanel(): void
}> = ({ currentTeam, session, onShowTeamSelectionMenuPanel }) => {
  const { endSession } = useAuth()
  const { doRefreshCheck } = useAppRefresh()

  const handleLogout = () => {
    endSession()

    // check to refresh the webapp when the user logs out
    doRefreshCheck()
  }

  const userSettingsMenuOptions = useGetUserSettingsMenuOptions()
  const firstEnabledOption = userSettingsMenuOptions.items[0]

  return (
    <MenuList data-testid="main-header-menu-panel">
      <Stack mb={2} pl={2} spacing={2}>
        <Stack alignItems="center" direction="row" spacing={2}>
          <Typography color={(muiTheme) => muiTheme.palette.text.primary}>
            <Icon css={{ width: 20 }} icon="solidUser" />
          </Typography>
          <Typography color="inherit" variant="inherit">
            {session.identifier}
          </Typography>
        </Stack>
        <Stack alignItems="center" direction="row" spacing={2}>
          <Typography color={(muiTheme) => muiTheme.palette.text.primary}>
            <Icon css={{ width: 20 }} icon="solidBuilding" />
          </Typography>
          <Typography color="inherit" variant="inherit">
            {session.company_name}
          </Typography>
        </Stack>
      </Stack>
      <Divider variant="middle" />
      <Stack mb={2} mt={2} pl={2} spacing={2}>
        <Typography color="inherit" variant="overline">
          CURRENT TEAM
        </Typography>
        <Stack alignItems="center" direction="row" spacing={2}>
          <Typography color={(muiTheme) => muiTheme.palette.text.primary}>
            <Icon css={{ width: 20 }} icon="solidUserGroup" />
          </Typography>
          <Typography color="inherit" variant="inherit">
            {currentTeam.name}
          </Typography>
        </Stack>
      </Stack>
      <MenuItem
        aria-label="Switch Team"
        data-testid="switch-team-button"
        onClick={onShowTeamSelectionMenuPanel}
      >
        <ListItemText inset>Switch Team</ListItemText>
        <ListItemIcon>
          <Icon icon="chevronRight" />
        </ListItemIcon>
      </MenuItem>
      <Divider variant="middle" />
      <Typography color="inherit" mb={2} mt={2} pl={2} variant="overline">
        SETTINGS
      </Typography>
      <MenuItem component={RouterLink} to="/settings/company/options">
        <ListItemText inset>Company</ListItemText>
      </MenuItem>
      <MenuItem
        component={RouterLink}
        to={`/settings/team/${currentTeam.identifier}/details`}
      >
        <ListItemText inset>Team</ListItemText>
      </MenuItem>
      {firstEnabledOption && (
        <MenuItem component={RouterLink} to={firstEnabledOption.url}>
          <ListItemText inset>User</ListItemText>
        </MenuItem>
      )}
      <MenuItem onClick={handleLogout}>
        <ListItemText>Log out</ListItemText>
      </MenuItem>
    </MenuList>
  )
}
